/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { UploadFile } from "@duxton-web/ui"

import { RETURN_MEDIA_MAX_PHOTO_SIZE_BYTES } from "ecommerce/constants/returnRefund"
import { pickNativeImage, PickImageOptions } from "utils/nativeBridge"

/**
 * Distinguishes native-picked entries in an Upload list from browser-picked ones,
 * and keeps their uids unique alongside the `rc-upload-*` uids Duxton generates.
 */
const NATIVE_PHOTO_UID_PREFIX = "native-photo-"

/*
 * Uniqueness within a render burst: two picks can land on the same millisecond
 * (the timestamp alone is not a safe uid), and the counter alone would restart
 * across a remount (colliding with entries kept in form state).
 */
let pickSequence = 0

/*
 * The spec says `pickImage` answers with RAW base64, but the app's actual
 * implementation (`delegate.encodeFile`) hands back a full data URL —
 * `data:image/jpeg;base64,/9j/...` (verified in the app debugger, 2026-08-27).
 * Accept both: `atob` throws on the data-URL prefix's `:` and `,`, which read
 * to the merchant as every photo failing.
 */
const DATA_URL_PATTERN = /^data:(image\/[a-z0-9+.-]+);base64,/i

/*
 * base64 spends 4 characters on every 3 bytes, so this is the longest encoding a
 * photo within the upload endpoint's limit can have (padding included).
 *
 * The gate runs BEFORE `atob`, not after: the size check on the finished
 * `UploadFile` only fires once the payload has already been decoded into a string,
 * a `Uint8Array`, a `File` and a preview data URL — four allocations proportional
 * to whatever the bridge sent. The app compresses well under the limit, so nothing
 * legitimate comes close; a malformed or compromised bridge answer would otherwise
 * exhaust the webview before any check could reject it.
 */
const MAX_ENCODED_LENGTH = Math.ceil(RETURN_MEDIA_MAX_PHOTO_SIZE_BYTES / 3) * 4

/** Rejects what `atob` would otherwise be handed: only `A–Z a–z 0–9 + / =`. */
const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/

/**
 * A native-picked photo as the `UploadFile` shape the return forms already hold,
 * so the picked photo rides the exact pipeline a browser-picked one does: the
 * Duxton list renders `thumbUrl`, and the staging upload reads `originFileObj`.
 *
 * The app hands back JPEG — `ChoosePhotoToolV2` re-encodes whatever the camera
 * or gallery supplies as part of its compression pass — but the mime is still
 * read from the data URL when one is supplied, so a PNG would survive too.
 *
 * Throws on a payload that is oversized or not base64, before decoding it.
 */
export const toNativePhotoUploadFile = (payload: string): UploadFile => {
  const dataUrlMatch = payload.match(DATA_URL_PATTERN)
  const mimeType = dataUrlMatch?.[1].toLowerCase() ?? "image/jpeg"
  const base64 = dataUrlMatch ? payload.slice(dataUrlMatch[0].length) : payload

  if (
    /*
     * Empty first: `data:image/jpeg;base64,` with nothing after it satisfies the
     * pattern below, and would decode to a zero-byte file that the size gate waves
     * through and the upload then stages as a photo carrying no image.
     */
    base64.length === 0 ||
    base64.length > MAX_ENCODED_LENGTH ||
    !BASE64_PATTERN.test(base64)
  ) {
    throw new Error("native photo payload is empty, oversized or not base64")
  }

  const bytes = atob(base64)
  const buffer = new Uint8Array(bytes.length)

  for (let index = 0; index < bytes.length; index += 1) {
    buffer[index] = bytes.charCodeAt(index)
  }

  pickSequence += 1
  const uid = `${NATIVE_PHOTO_UID_PREFIX}${Date.now()}-${pickSequence}`
  const extension = mimeType === "image/png" ? "png" : "jpeg"
  const file = new File([buffer], `${uid}.${extension}`, { type: mimeType })

  // Duxton's `originFileObj` is an `RcFile` — a `File` carrying the list uid.
  Object.assign(file, { uid })

  return {
    uid,
    name: file.name,
    size: file.size,
    type: file.type,
    originFileObj: file as UploadFile["originFileObj"],
    // The picked bytes as their own preview: `thumbUrl` spares Duxton's list
    // from re-reading the blob to render the picture card.
    thumbUrl: `data:${mimeType};base64,${base64}`,
  }
}

/**
 * One photo from the app's camera or gallery, ready to append to an Upload list.
 *
 * Resolves `null` when there is nothing to add (cancelled, permission refused,
 * or no bridge), exactly as {@link pickNativeImage} does; a genuine failure
 * rejects so the caller can toast it.
 */
export const pickNativePhotoUploadFile = async (
  source: PickImageOptions["source"],
): Promise<UploadFile | null> => {
  const base64 = await pickNativeImage({ source })

  return base64 ? toNativePhotoUploadFile(base64) : null
}
